import React, { Component } from 'react';

import qs from 'qs';
import AppRoutes from '../routes';
import PropTypes from 'prop-types';
import { v4 as uuidv4 } from 'uuid';
import { connect } from 'react-redux';
import { Snackbar } from 'components/UI';
import packageInfo from '../../package.json';
import UAParserContructor from 'ua-parser-js';
import ThemeLegacy from '../theme';
import Theme from '../theme/theme';
import { apiAxiosInstance } from 'services/api';
import { AuthActions } from 'state/modules/auth';
import { ThemeProvider } from '@mui/material/styles';
import { integrations } from '../config/integrations';
import { ChangePasswordModal } from 'components/Modals';
import { clearStorageItems, hasAuthCookie } from 'utils';
import { UserSettingsActions } from 'state/modules/userSettings';
import { ThemeProvider as ThemeProviderLegacy } from '@material-ui/core/styles';
import { UserContextProvider, OrganizationProvider, AuthContextProvider } from 'context';

window.global = window;

const UAParser = new UAParserContructor();

class Root extends Component {
  constructor(props) {
    super(props);
    this.state = {
      openPasswordModal: false,
      isRefreshing: false,
      failedQueue: [],
    };
  }

  processQueue = (error, token = null) => {
    this.state.failedQueue.forEach((prom) => {
      if (error) {
        prom.reject(error);
      } else {
        prom.resolve(token);
      }
    });

    this.setState({ failedQueue: [] });
  };

  logout = () => {
    const { dispatch } = this.props;
    clearStorageItems();
    dispatch({ type: 'FETCH_LOGIN_FAILED' });
  };

  handleModalOpen = (request) => {
    const { dispatch } = this.props;
    this.setState((prevState) => ({
      openPasswordModal: !prevState.openPasswordModal,
    }));
    if (!request) {
      dispatch(AuthActions.postLogin());
      this.processQueue(null, localStorage.getItem('access_token'));
    }
  };

  getOs = () => {
    let name = UAParser.getOS().name;
    if (/windows/i.test(name)) name = 'windows';
    else if (name === 'Mac OS') name = 'macos';
    else if (name) name = name.replace(' ', '-').toLowerCase();
    return name || 'unknown';
  };

  handleInterceptor = () => {
    // Request interceptor
    apiAxiosInstance.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('access_token');
        const refreshToken = localStorage.getItem('refresh_token');

        if (
          token &&
          ![
            '/auth/login',
            '/auth/logout',
            '/auth/refresh',
            '/auth/login/transfer',
            '/organization/public',
          ].includes(config.url)
        ) {
          config.headers['Authorization'] = `Bearer ${token}`;
        }

        if (refreshToken && (config.url === '/auth/refresh' || config.url === '/auth/logout')) {
          config.headers['Authorization'] = `Bearer ${refreshToken}`;
        }

        let org_slug = sessionStorage.getItem('org_slug');
        if (
          ![
            '/login',
            '/pages/password/request',
            '/pages/password/change',
            '/pages/email/confirm',
            '/mfa',
          ].includes(window.location.pathname)
        ) {
          let currentOrg = window.location.pathname.split('/')[1];
          if (!currentOrg && this.props.user?.id) {
            const query = qs.parse(window.location.search.substring(1));

            if (
              'success' in query &&
              query.field_passthrough1 &&
              query.field_passthrough3 &&
              query.tenantId
            ) {
              currentOrg = query.field_passthrough3;
              window.location = `${window.location.origin}/${currentOrg}/${
                query.field_passthrough1
              }/${query.field_passthrough2}?success=${query.success}&refId=${
                query.refId || ''
              }&token=${query.token || ''}`;
            } else if (org_slug) {
              currentOrg = org_slug;
              window.location = window.location.origin + '/' + currentOrg;
            }
          }
          if (!currentOrg?.startsWith('?')) {
            org_slug = currentOrg;
          }
        }

        if (org_slug) {
          config.headers['Org-Slug'] = org_slug;
        }

        let unique_id = localStorage.getItem('unique_id');
        if (unique_id === null) {
          unique_id = uuidv4();
          localStorage.setItem('unique_id', unique_id);
        }

        const clientId = localStorage.getItem('client_id');

        // Add API-Agent
        let apiAgent = `client=mc3; version=${packageInfo.version}; platform=web; device_os=${this.getOs()}; target_version=${integrations.apiTargetVersion}; unique_id=${unique_id}; is_dev=${integrations.deployment !== 'production'}`;
        if (clientId) {
          apiAgent += `; client_id=${clientId}`;
        }
        config.headers['API-Agent'] = apiAgent;

        if (hasAuthCookie()) {
          // Set withCredentials to true only for credential-required requests
          config.withCredentials = true;
        }

        return config;
      },
      function (err) {
        return Promise.reject(err);
      }
    );

    // Response interceptor
    apiAxiosInstance.interceptors.response.use(
      (response) => {
        return response;
      },
      (err) => {
        // Any status codes that falls outside the range of 2xx cause this function to trigger

        let originalRequest = err.config;
        let error = err.response || err;
        const { isRefreshing } = this.state;

        if (error?.data?.key === 'PASSWORDEXPIRED' && !this.state.openPasswordModal) {
          this.handleModalOpen(true);
        }

        if (error?.data?.key === 'REFRESHTOKENEXPIRED' && !this.state.openPasswordModal) {
          this.logout();
        }

        if (error?.data?.key === 'REVOKEDTOKEN' && !this.state.openPasswordModal) {
          this.logout();
        }

        if (!originalRequest._retry && isRefreshing) {
          return new Promise((resolve, reject) => {
            this.setState((prevState) => ({
              failedQueue: [...prevState.failedQueue, { resolve, reject }],
            }));
          })
            .then(() => {
              return apiAxiosInstance(originalRequest);
            })
            .catch((err) => {
              return Promise.reject(err);
            });
        }

        if (error?.data?.key === 'PASSWORDEXPIRED' && !this.state.openPasswordModal) {
          originalRequest._retry = true;
          this.setState({ isRefreshing: true });
        } else if (error?.status === 401) {
          console.log('401 Error', error?.data);
          console.log('originalRequest', originalRequest);

          if (!hasAuthCookie()) {
            if (error?.data?.key === 'ACCESSTOKENEXPIRED' && !originalRequest._retry) {
              originalRequest._retry = true;
              this.setState({ isRefreshing: true });

              return new Promise((resolve, reject) => {
                const payload = {
                  access_token: localStorage.getItem('refresh_token'),
                  enabled: true,
                };
                apiAxiosInstance
                  .post('/auth/refresh', payload)
                  .then((response) => {
                    localStorage.setItem('access_token', response?.data?.access_token || '');
                    this.processQueue(null, response?.data?.access_token || '');
                    resolve(apiAxiosInstance(originalRequest));
                  })
                  .catch((err) => {
                    this.logout();
                    this.processQueue(err, null);
                    reject(err);
                  })
                  .then(() => {
                    this.setState({ isRefreshing: false });
                  });
              });
            } else if (error?.data?.key !== 'UNAUTHORIZED') {
              this.logout();
            }
          } else {
            this.logout();
            return Promise.reject(err);
          }
        }
        return Promise.reject(err);
      }
    );
  };

  addUseChatScript = () => {
    const script = document.createElement('script');
    script.id = 'hs-script-loader';
    script.src = '//js.hs-scripts.com/4196702.js';
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);
  };

  componentDidMount() {
    const { dispatch } = this.props;
    const query = qs.parse(window.location.search.substring(1));
    const { token, org } = query;
    this.handleInterceptor();
    const authPages = ['/pages/password/change', '/pages/email/confirm'];
    if (token && !authPages.includes(window.location.pathname)) {
      dispatch(AuthActions.fetchAccessToken({ token }, org));
    }
    dispatch(UserSettingsActions.handleShowGuidingModal('INIT'));
  }

  componentDidUpdate() {
    const scriptElement = document.getElementById('hs-script-loader');
    if (!this.props.user) {
      if (scriptElement) scriptElement.remove();
    }
    if (this.props.user?.id && !scriptElement) {
      this.addUseChatScript();
    }
  }

  render() {
    const { user, config } = this.props;

    return (
      <AuthContextProvider>
        <ThemeProviderLegacy theme={ThemeLegacy}>
          <ThemeProvider theme={Theme}>
            <UserContextProvider user={user} config={config}>
              <OrganizationProvider>
                <AppRoutes />
                <Snackbar />
                {this.state.openPasswordModal && (
                  <ChangePasswordModal
                    open={this.state.openPasswordModal}
                    handleClick={this.handleModalOpen}
                  />
                )}
              </OrganizationProvider>
            </UserContextProvider>
          </ThemeProvider>
        </ThemeProviderLegacy>
      </AuthContextProvider>
    );
  }
}

const mapStateToProps = (state) => ({
  user: state.auth.user,
  config: state.config?.settings?.core,
});

Root.propTypes = {
  user: PropTypes.object,
  config: PropTypes.object,
  dispatch: PropTypes.func.isRequired,
};

export default connect(mapStateToProps)(Root);
